// Router del admin-portal. Rutas:
//   /login              — pagina publica de login
//   /dashboard          — protegida (placeholder en M9.1)
//   /users              — M9.3
//   /partners           — M9.5
//   /jobs               — M9.4
//   /audit              — M9.3
//   /                   — redirect al login, o a la primera pantalla que el
//                         rol pueda ver (ver lib/access.ts)

import { Navigate, Route, Routes } from 'react-router-dom';
import AdminShell from '@/components/AdminShell';
import ProtectedRoute from '@/components/ProtectedRoute';
import AdminLogin from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Users from '@/pages/Users';
import AuditLog from '@/pages/AuditLog';
import Jobs from '@/pages/Jobs';
import PendingCharges from '@/pages/PendingCharges';
import Partners from '@/pages/Partners';
import PlanDetail from '@/pages/PlanDetail';
import Reports from '@/pages/Reports';
import Admins from '@/pages/Admins';
import AccessProfiles from '@/pages/AccessProfiles';
import Coupons from '@/pages/Coupons';
import Invoices from '@/pages/Invoices';
import Account from '@/pages/Account';
import DisposableDomains from '@/pages/DisposableDomains';
import NotFound from '@/pages/NotFound';
import CatalogShell from '@/components/CatalogShell';
import CatalogTypes from '@/pages/CatalogTypes';
import CatalogTypeEdit from '@/pages/CatalogTypeEdit';
import CatalogItems from '@/pages/CatalogItems';
import CatalogItemEdit from '@/pages/CatalogItemEdit';
import { useSession } from '@/hooks/useSession';
import { canSee, landingPath, type Section } from '@/lib/access';

function HomeRedirect(): JSX.Element {
  const session = useSession();
  return <Navigate to={session ? landingPath(session) : '/login'} replace />;
}

// Puerta por seccion. Quien ve que sale de lib/access.ts — el mismo modulo que
// dibuja el menu, para que esconder un link y bloquear su URL no puedan
// separarse. Cupones ademas del rol exige el permiso danger_zone; eso tambien
// esta en la tabla. El backend revalida en cada endpoint: esto es solo UX.
//
// El rebote va a la primera pantalla que el rol SI puede ver. Mandar a todos a
// /dashboard dejaria a `support` rebotando contra otra pantalla prohibida.
function SectionGate({
  section,
  children,
}: {
  section: Section;
  children: JSX.Element;
}): JSX.Element {
  const session = useSession();
  if (!canSee(session, section)) {
    return <Navigate to={landingPath(session)} replace />;
  }
  return children;
}

export default function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<AdminLogin />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="dashboard">
                <Dashboard />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/*"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="users">
                <Users />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cobros"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="users">
                <PendingCharges />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners/*"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="partners">
                <Partners />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      {/* Reportes contables derivados del ledger. */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="reports">
                <Reports />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />

      {/* Facturacion electronica (ARCA/Facturante). La ven admin y support —
          soporte necesita el comprobante cuando el usuario llama por el. Las
          acciones que reparan una factura (reintentar, reenviar) siguen siendo
          de admin: las esconde la pagina y las rechaza el backend. */}
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="invoices">
                <Invoices />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />

      {/* Detalle de plan: precio vigente + linea de tiempo de versiones.
          Se llega desde el tab "plans" de un partner. */}
      <Route
        path="/plans/:planId"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="partners">
                <PlanDetail />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="catalog">
                <CatalogShell />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/catalog/types" replace />} />
        <Route path="types" element={<CatalogTypes />} />
        <Route path="types/new" element={<CatalogTypeEdit />} />
        <Route path="types/:typeId" element={<CatalogTypeEdit />} />
        <Route path="items" element={<CatalogItems />} />
        <Route path="items/new" element={<CatalogItemEdit />} />
        <Route path="items/:favoriteId" element={<CatalogItemEdit />} />
      </Route>

      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="jobs">
                <Jobs />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="audit">
                <AuditLog />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="team">
                <Admins />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      {/* Perfiles de acceso: que ve cada cuenta. Misma seccion que Equipo. */}
      <Route
        path="/team/profiles"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="team">
                <AccessProfiles />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coupons"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="coupons">
                <Coupons />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/disposable-domains"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="disposable">
                <DisposableDomains />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AdminShell>
              <SectionGate section="account">
                <Account />
              </SectionGate>
            </AdminShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="*"
        element={
          <AdminShell>
            <NotFound />
          </AdminShell>
        }
      />
    </Routes>
  );
}
